import type { FetchAttributeValue, FetchElement } from "./model.js";

export type HierarchicalRelationCardinality = "many-to-one" | "one-to-many" | "many-to-many";

export interface HierarchicalRelationHint {
  alias: string;
  parentAlias: "root" | string;
  cardinality: HierarchicalRelationCardinality;
  targetEntityLogicalName: string;
  fromAttribute: string;
  toAttribute: string;
  manyToMany?: {
    intersectEntityLogicalName: string;
    peerEntityLogicalName: string;
    localIntersectAttribute: string;
    peerIntersectAttribute: string;
    peerPrimaryIdAttribute?: string;
  };
}

export interface HierarchicalPlanOptions {
  rootPrimaryKeyAttribute: string;
  rootBatchSize?: number;
  inBatchSize?: number;
}

export interface HierarchicalRelationQueryPlan extends HierarchicalRelationHint {
  fetchElement: FetchElement;
}

export interface HierarchicalFetchPlan {
  rootFetchElement: FetchElement;
  rootPrimaryKeyAttribute: string;
  rootBatchSize: number;
  inBatchSize: number;
  relationPlans: HierarchicalRelationQueryPlan[];
}

export interface ComposeHierarchicalRowsInput {
  rootRows: ReadonlyArray<Record<string, unknown>>;
  relationRowsByAlias: Record<string, ReadonlyArray<Record<string, unknown>>>;
  relationHints: ReadonlyArray<HierarchicalRelationHint>;
  rootPrimaryKeyAttribute: string;
}

function cloneFetchElement(node: FetchElement): FetchElement {
  return {
    name: node.name,
    attributes: { ...node.attributes },
    children: node.children.map((child) => cloneFetchElement(child)),
    text: node.text,
  };
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  if (!value || !Number.isFinite(value) || value < 1) {
    return fallback;
  }

  return Math.floor(value);
}

function getRootEntityNode(fetchElement: FetchElement): FetchElement {
  const rootEntity = fetchElement.children.find((child) => child.name === "entity");
  if (!rootEntity) {
    throw new Error("FetchXML root entity is missing.");
  }

  return rootEntity;
}

function hasAttributeSelection(entityNode: FetchElement, attributeName: string): boolean {
  return entityNode.children.some(
    (child) => child.name === "attribute" && String(child.attributes.name || "") === attributeName,
  );
}

function ensureEntityAttribute(entityNode: FetchElement, attributeName: string): void {
  if (hasAttributeSelection(entityNode, attributeName)) {
    return;
  }

  entityNode.children.unshift({
    name: "attribute",
    attributes: { name: attributeName },
    children: [],
  });
}

function copyFetchAttributes(source: FetchElement): Record<string, FetchAttributeValue> {
  const next: Record<string, FetchAttributeValue> = {
    version: source.attributes.version || "1.0",
    mapping: source.attributes.mapping || "logical",
  };

  if (source.attributes["output-format"]) {
    next["output-format"] = source.attributes["output-format"];
  }

  if (source.attributes.distinct !== undefined) {
    next.distinct = source.attributes.distinct;
  }

  if (source.attributes["no-lock"] !== undefined) {
    next["no-lock"] = source.attributes["no-lock"];
  }

  return next;
}

function getChildFanOutHints(
  relationHints: ReadonlyArray<HierarchicalRelationHint>,
  parentAlias: "root" | string,
): HierarchicalRelationHint[] {
  return relationHints.filter(
    (relation) => relation.parentAlias === parentAlias && relation.cardinality !== "many-to-one",
  );
}

function ensureParentCorrelationAttributes(
  node: FetchElement,
  relationHints: ReadonlyArray<HierarchicalRelationHint>,
  parentAlias: "root" | string,
): void {
  getChildFanOutHints(relationHints, parentAlias).forEach((childRelation) => {
    ensureEntityAttribute(node, childRelation.toAttribute);
  });
}

function buildRelationFetchElement(rootFetchElement: FetchElement, linkEntityNode: FetchElement): FetchElement {
  return {
    name: "fetch",
    attributes: copyFetchAttributes(rootFetchElement),
    children: [
      {
        name: "entity",
        attributes: {
          name: String(linkEntityNode.attributes.name || ""),
        },
        children: linkEntityNode.children.map((child) => cloneFetchElement(child)),
      },
    ],
  };
}

function toLookupKey(value: unknown): string {
  if (value === undefined || value === null) {
    return "";
  }

  return String(value);
}

function readDataverseAttributeValue(row: Record<string, unknown>, attributeName: string): unknown {
  if (!attributeName) {
    return undefined;
  }

  if (Object.prototype.hasOwnProperty.call(row, attributeName)) {
    return row[attributeName];
  }

  if (attributeName.includes(".")) {
    return undefined;
  }

  const lookupAttributeName =
    attributeName.startsWith("_") && attributeName.endsWith("_value")
      ? attributeName
      : `_${attributeName}_value`;

  if (lookupAttributeName !== attributeName && Object.prototype.hasOwnProperty.call(row, lookupAttributeName)) {
    return row[lookupAttributeName];
  }

  return undefined;
}

function assignPath(target: Record<string, unknown>, segments: string[], value: unknown): void {
  if (segments.length === 0) {
    return;
  }

  let cursor: Record<string, unknown> = target;
  const lastIndex = segments.length - 1;

  for (let index = 0; index < lastIndex; index += 1) {
    const segment = segments[index];
    const existing = cursor[segment];
    if (!existing || typeof existing !== "object" || Array.isArray(existing)) {
      cursor[segment] = {};
    }

    cursor = cursor[segment] as Record<string, unknown>;
  }

  cursor[segments[lastIndex]] = value;
}

function inflateDataverseRow(row: Record<string, unknown>): Record<string, unknown> {
  const inflated: Record<string, unknown> = {};

  Object.entries(row).forEach(([key, value]) => {
    if (key.startsWith("@") || !key.includes(".")) {
      inflated[key] = value;
      return;
    }

    assignPath(inflated, key.split("."), value);
  });

  return inflated;
}

export function splitInBatches<T>(values: ReadonlyArray<T>, batchSize: number): T[][] {
  const normalizedBatchSize = normalizePositiveInteger(batchSize, 1000);
  const batches: T[][] = [];

  for (let index = 0; index < values.length; index += normalizedBatchSize) {
    batches.push(values.slice(index, index + normalizedBatchSize));
  }

  return batches;
}

export function planHierarchicalFetch(
  fetchElement: FetchElement,
  relationHints: ReadonlyArray<HierarchicalRelationHint>,
  options: HierarchicalPlanOptions,
): HierarchicalFetchPlan {
  const rootBatchSize = normalizePositiveInteger(options.rootBatchSize, 500);
  const inBatchSize = normalizePositiveInteger(options.inBatchSize, 1000);
  const rootFetchElement = cloneFetchElement(fetchElement);
  const rootEntityNode = getRootEntityNode(rootFetchElement);
  const relationHintsByAlias = new Map(relationHints.map((relation) => [relation.alias, relation]));
  const relationPlans: HierarchicalRelationQueryPlan[] = [];

  if (!rootFetchElement.attributes.count) {
    rootFetchElement.attributes.count = String(rootBatchSize);
  }

  ensureEntityAttribute(rootEntityNode, options.rootPrimaryKeyAttribute);
  ensureParentCorrelationAttributes(rootEntityNode, relationHints, "root");

  const splitNode = (node: FetchElement, parentAlias: "root" | string): void => {
    ensureParentCorrelationAttributes(node, relationHints, parentAlias);
    const nextChildren: FetchElement[] = [];

    node.children.forEach((child) => {
      if (child.name !== "link-entity") {
        nextChildren.push(child);
        return;
      }

      const alias = String(child.attributes.alias || "").trim();
      const relation = relationHintsByAlias.get(alias);

      if (!relation) {
        nextChildren.push(child);
        return;
      }

      const splitChild = relation.cardinality !== "many-to-one";
      if (splitChild) {
        const relationFetchElement = buildRelationFetchElement(rootFetchElement, child);
        const relationEntityNode = getRootEntityNode(relationFetchElement);
        ensureEntityAttribute(relationEntityNode, relation.fromAttribute);
        splitNode(relationEntityNode, relation.alias);

        relationPlans.push({
          ...relation,
          fetchElement: relationFetchElement,
        });
        return;
      }

      splitNode(child, relation.alias);
      nextChildren.push(child);
    });

    node.children = nextChildren;
  };

  splitNode(rootEntityNode, "root");

  return {
    rootFetchElement,
    rootPrimaryKeyAttribute: options.rootPrimaryKeyAttribute,
    rootBatchSize,
    inBatchSize,
    relationPlans,
  };
}

export function createRelationBatchFetch(
  relationPlan: HierarchicalRelationQueryPlan,
  parentIds: ReadonlyArray<string | number | boolean>,
): FetchElement {
  const next = cloneFetchElement(relationPlan.fetchElement);
  const rootEntity = getRootEntityNode(next);

  const conditionNode: FetchElement = {
    name: "condition",
    attributes: {
      attribute: relationPlan.fromAttribute,
      operator: "in",
    },
    children: parentIds.map((parentId) => ({
      name: "value",
      attributes: {},
      children: [],
      text: String(parentId),
    })),
  };

  rootEntity.children.push({
    name: "filter",
    attributes: { type: "and" },
    children: [conditionNode],
  });

  return next;
}

export function composeHierarchicalRows(input: ComposeHierarchicalRowsInput): Array<Record<string, unknown>> {
  const fanOutRelations = input.relationHints.filter(
    (relation) => relation.cardinality !== "many-to-one",
  );
  const rowsByAliasAndParentKey = new Map<string, Map<string, Array<Record<string, unknown>>>>();
  const fanOutRelationsByParentAlias = new Map<string, HierarchicalRelationHint[]>();

  fanOutRelations.forEach((relation) => {
    const current = fanOutRelationsByParentAlias.get(relation.parentAlias) || [];
    current.push(relation);
    fanOutRelationsByParentAlias.set(relation.parentAlias, current);
  });

  fanOutRelations.forEach((relation) => {
    const rows = input.relationRowsByAlias[relation.alias] || [];
    const rowsByParentKey = new Map<string, Array<Record<string, unknown>>>();

    rows.forEach((row) => {
      const key = toLookupKey(readDataverseAttributeValue(row, relation.fromAttribute));
      if (!rowsByParentKey.has(key)) {
        rowsByParentKey.set(key, []);
      }

      rowsByParentKey.get(key)?.push(row);
    });

    rowsByAliasAndParentKey.set(relation.alias, rowsByParentKey);
  });

  const applyChildren = (
    hydratedParentRow: Record<string, unknown>,
    parentRow: Record<string, unknown>,
    parentAlias: "root" | string,
  ): void => {
    const relations = fanOutRelationsByParentAlias.get(parentAlias) || [];

    relations.forEach((relation) => {
      const parentKey = toLookupKey(
        readDataverseAttributeValue(parentRow, relation.toAttribute) ??
          readDataverseAttributeValue(parentRow, input.rootPrimaryKeyAttribute),
      );
      const relatedRows = rowsByAliasAndParentKey.get(relation.alias)?.get(parentKey) || [];
      const hydratedChildren = relatedRows.map((row) => {
        const hydrated = inflateDataverseRow(row);
        applyChildren(hydrated, row, relation.alias);
        return hydrated;
      });

      hydratedParentRow[relation.alias] = hydratedChildren;
    });
  };

  return input.rootRows.map((rootRow) => {
    const hydrated = inflateDataverseRow(rootRow);

    applyChildren(hydrated, rootRow, "root");

    return hydrated;
  });
}
